import React, { useState, useEffect } from 'react';
import supabase from '../config/supabase';
import { getTemplateColor } from '../utils/templateColor';

const ArchiveView = ({ onClose, onUnarchive }) => {
  const [archivedTemplates, setArchivedTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArchivedTemplates();
  }, []);

  const loadArchivedTemplates = async () => {
    setLoading(true);
    try {
      // Get current user and store
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: storeData } = await supabase
        .from('store_users')
        .select('store_id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (!storeData) return;

      // Load archived templates
      const { data: templatesData, error } = await supabase
        .from('count_templates')
        .select('*')
        .eq('store_id', storeData.store_id)
        .eq('is_active', true)
        .eq('is_archived', true)
        .order('archived_at', { ascending: false });

      if (error) {
        console.error('Error loading archived templates:', error);
        return;
      }

      // Load template items for each archived template
      const templatesWithItems = await Promise.all(
        (templatesData || []).map(async (template) => {
          const { data: templateItems } = await supabase
            .from('count_template_items')
            .select('inventory_item_id')
            .eq('template_id', template.id)
            .eq('include_in_count', true);

          const itemIds = templateItems?.map(item => item.inventory_item_id) || [];
          return { ...template, items: itemIds };
        })
      );

      setArchivedTemplates(templatesWithItems);
    } catch (error) {
      console.error('Error loading archived templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (templateId) => {
    try {
      await onUnarchive(templateId);
      // Reload archived templates to remove the unarchived one
      loadArchivedTemplates();
    } catch (error) {
      console.error('Error unarchiving template:', error);
    }
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="feather icon-archive me-2"></i>
              Archived Templates ({archivedTemplates.length})
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading archived templates...</p>
              </div>
            ) : archivedTemplates.length === 0 ? (
              <div className="alert alert-info">
                <i className="feather icon-info-circle me-2"></i>
                No archived templates found. Templates you archive will appear here.
              </div>
            ) : (
              <div className="row">
                {archivedTemplates.map(template => {
                  const archivedDate = template.archived_at
                    ? new Date(template.archived_at).toLocaleDateString()
                    : 'Unknown';
                  const accentColor = getTemplateColor(template);

                  return (
                    <div key={template.id} className="col-md-6 mb-3">
                      <div className="card" style={{
                        backgroundColor: '#F8F9FA',
                        border: '1px solid #DEE2E6',
                        borderLeft: `4px solid ${accentColor}`,
                        opacity: 0.8
                      }}>
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="card-title mb-0" style={{ color: accentColor }}>
                              {template.name}
                            </h6>
                            <span className="badge bg-secondary">Archived</span>
                          </div>
                          
                          <p className="small mb-2" style={{ color: '#6C757D' }}>
                            <i className="feather icon-package me-1"></i>
                            <strong>{template.items.length}</strong> items
                          </p>
                          
                          {template.category && template.category !== 'custom' && (
                            <p className="small mb-2" style={{ color: '#6C757D' }}>
                              <i className="feather icon-tag me-1"></i>
                              {template.category}
                            </p>
                          )}
                          
                          <p className="small mb-2" style={{ color: '#6C757D' }}>
                            <i className="feather icon-archive me-1"></i>
                            Archived: {archivedDate}
                          </p>
                          
                          <div className="d-flex gap-2 mt-3">
                            <button
                              className="btn btn-primary btn-sm flex-fill"
                              onClick={() => handleUnarchive(template.id)}
                            >
                              <i className="feather icon-rotate-ccw me-1"></i>
                              Restore
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchiveView;